import {useCallback, useEffect, useMemo, useState} from "react";
import {Modal} from "../../../components";
import {formatApiError, listTeams} from "../../../api/admin";
import {isDebtCabinetMerchant} from "../../../api/admin/merchants";
import type {MerchantResponse} from "../../../api/admin/merchants";
import {getMerchantBalanceByTeam} from "../../../api/admin/merchantBalance";
import {
    getPlatformClosedOrders,
    settlePlatformClosedOrder,
} from "../../../api/admin/platformClosedOrders";
import type {PlatformClosedOrderItem} from "../../../api/admin/platformClosedOrders";
import {formatDate} from "../../../utils";
import {suggestSettlementTeam} from "./platformClosed";

/**
 * Заявки, которые площадка закрыла и оплатила кабинету, а мы не разнесли.
 *
 * ЗАЧЕМ РАЗДЕЛ. Невзятую заявку, закрытую руками в кабинете площадки, контур
 * убирает из пула отменой, и дальше она неотличима от протухшей. Платежа у неё
 * нет, значит нет ни минуса мерчанту, ни начисления команде — а кабинету
 * площадка заплатила. Раньше такую заявку не показывал никто, и находили её
 * только по выросшему дрейфу, задним числом.
 *
 * ПОЧЕМУ ЗДЕСЬ, А НЕ В ЗАЯВКАХ. Сумма считается по книге ЭТОГО кабинета, обе
 * стороны проводки адресуются балансом ЭТОГО мерчанта, и равенство «долг
 * кабинета = срез команды по нему», ради которого действие существует, читается
 * на этой же странице соседними блоками. Раздел стоит там, где стоит его
 * предмет.
 *
 * ПОЧЕМУ КОМАНДУ ВЫБИРАЕТ ЧЕЛОВЕК. Система не знает, кто выполнил заявку в
 * кабинете: она не проходила через нашего трейдера вовсе. Подставленная по
 * умолчанию команда была бы догадкой, за которую платят деньгами, поэтому
 * подсказка есть, а выбора по умолчанию нет.
 *
 * Все суммы — строки с бэкенда и в число не переводятся: у денег четыре знака
 * на стороне площадки и шесть на нашей.
 */
export function PlatformClosedOrdersSection({
    merchant,
    token,
    onSettled,
}: {
    merchant: MerchantResponse;
    token: string | null;
    onSettled: (message: string) => void;
}) {
    const debtCabinet = isDebtCabinetMerchant(merchant);
    const [orders, setOrders] = useState<PlatformClosedOrderItem[]>([]);
    const [total, setTotal] = useState<string>("0");
    const [currency, setCurrency] = useState<string>("USDT");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [teams, setTeams] = useState<{id: string; name: string}[]>([]);
    // Подсказка, а не выбор: команда, которая уже держит срез по этому мерчанту.
    // Ровно её брал разовый скрипт, и ровно она в подавляющем большинстве
    // случаев верна — но выбрать её обязан человек.
    const [suggested, setSuggested] = useState<string | null>(null);

    const [settling, setSettling] = useState<PlatformClosedOrderItem | null>(null);
    const [teamId, setTeamId] = useState("");
    const [busy, setBusy] = useState(false);
    const [settleError, setSettleError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!token || !debtCabinet) return;
        setLoading(true);
        setError(null);
        try {
            const list = await getPlatformClosedOrders(token, merchant.id);
            setOrders(list.orders);
            setTotal(list.total);
            setCurrency(list.currency);
        } catch (cause) {
            setError(formatApiError(cause));
        } finally {
            setLoading(false);
        }
    }, [token, debtCabinet, merchant.id]);

    useEffect(() => {
        void load();
    }, [load]);

    // Команды подгружаются только когда разделу есть что показать: список команд
    // — чужая страница, и дёргать её на каждой карточке мерчанта незачем.
    useEffect(() => {
        if (!token || !debtCabinet || orders.length === 0) return;
        let cancelled = false;
        void (async () => {
            try {
                const [all, byTeam] = await Promise.all([
                    listTeams(token, {limit: 200}),
                    getMerchantBalanceByTeam(token, merchant.id),
                ]);
                if (cancelled) return;
                setTeams(all.items.map((team) => ({id: team.id, name: team.name})));
                setSuggested(suggestSettlementTeam(byTeam.items));
            } catch {
                // Не показываем ошибку: без списка команд раздел остаётся
                // читаемым, а действие честно откажет «команда не выбрана».
                if (!cancelled) setTeams([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, debtCabinet, merchant.id, orders.length]);

    const suggestedName = useMemo(
        () => teams.find((team) => team.id === suggested)?.name ?? null,
        [teams, suggested],
    );

    // У мерчанта, который не долговой кабинет, случая не существует: там баланс
    // и так следует за кабинетом либо считается нами по нашим заявкам.
    if (!debtCabinet) return null;

    function openSettle(order: PlatformClosedOrderItem) {
        setSettling(order);
        setSettleError(null);
        setTeamId(suggested ?? "");
    }

    async function confirmSettle() {
        if (!token || !settling) return;
        setBusy(true);
        setSettleError(null);
        try {
            const result = await settlePlatformClosedOrder(token, merchant.id, settling.externalId, teamId);
            setSettling(null);
            onSettled(
                `Заявка ${result.order.externalId} разнесена: долг мерчанта углублён на ` +
                    `${result.order.amount} ${result.currency}, та же сумма начислена команде «${result.teamName}».`,
            );
            await load();
        } catch (cause) {
            setSettleError(formatApiError(cause));
        } finally {
            setBusy(false);
        }
    }

    return (
        <section className="merchant-rate-block merchant-platform-closed-block">
            <div className="section-header">
                <div>
                    <h3>Заявки, закрытые площадкой</h3>
                    <p className="section-hint">
                        Заявки кабинета, которые никто из трейдеров не взял, а площадка закрыла и
                        заплатила за них кабинету. Платежа у нас по ним нет, поэтому ни минуса
                        мерчанту, ни начисления команде не написано ничем: их пишет это действие,
                        обе стороны одной транзакцией. Сумма берётся из книги кабинета, а не из
                        карточки заявки — при перерасчёте площадки они расходятся, а платит книга.
                    </p>
                </div>
                <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => void load()}
                    disabled={loading}
                >
                    {loading ? "Читаем…" : "Обновить"}
                </button>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            {!loading && orders.length === 0 && (
                <p className="section-hint platform-closed-empty">
                    Неразнесённых заявок нет.
                </p>
            )}

            {orders.length > 0 && (
                <>
                    <table className="platform-closed-table">
                        <thead>
                            <tr>
                                <th>Заявка площадки</th>
                                <th>Закрыта</th>
                                <th>Площадка заплатила кабинету</th>
                                <th>Строки книги</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.externalId}>
                                    <td className="mono">{order.externalId}</td>
                                    <td>{formatDate(order.paidAt)}</td>
                                    <td className="mono">
                                        {order.amount} {currency}
                                    </td>
                                    <td className="mono">{order.bookRows.join(", ")}</td>
                                    <td>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            type="button"
                                            onClick={() => openSettle(order)}
                                        >
                                            Провести
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="section-hint">
                        Всего не разнесено: <strong className="platform-closed-total">{total} {currency}</strong>.
                        Ровно на эту сумму разошлись долг кабинета и срез команды по нему.
                    </p>
                </>
            )}

            <Modal
                open={settling !== null}
                title="Разноска заявки, закрытой площадкой"
                onClose={() => setSettling(null)}
                sizeClassName="modal-sm"
            >
                {settling && (
                    <>
                        <p className="section-hint">
                            Заявка <strong className="mono">{settling.externalId}</strong>: площадка
                            заплатила кабинету <strong className="mono">{settling.amount} {currency}</strong>.
                            Долг мерчанта углубится ровно на неё, и та же сумма будет начислена
                            выбранной команде. Обе стороны пишутся одной транзакцией.
                        </p>
                        <label className="form-field">
                            <span>Команда</span>
                            <select
                                value={teamId}
                                onChange={(event) => setTeamId(event.target.value)}
                                disabled={busy}
                            >
                                <option value="">— выберите команду —</option>
                                {teams.map((team) => (
                                    <option key={team.id} value={team.id}>
                                        {team.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {suggestedName && (
                            <p className="section-hint">
                                По этому мерчанту срез держит только «{suggestedName}» — скорее всего
                                заявку выполнила она. Решение всё равно за вами: кто закрыл заявку в
                                кабинете площадки, система не знает.
                            </p>
                        )}
                        <p className="section-hint">
                            Ключ проводки: <span className="mono">{settling.correctionKey}</span>. Он
                            уникален во всей таблице, поэтому повторное действие по этой заявке
                            вторую проводку не напишет.
                        </p>
                        {settleError && <div className="form-error" role="alert">{settleError}</div>}
                        <div className="modal-actions">
                            <button
                                className="btn btn-ghost btn-sm"
                                type="button"
                                onClick={() => setSettling(null)}
                                disabled={busy}
                            >
                                Отмена
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                onClick={() => void confirmSettle()}
                                disabled={busy || teamId === ""}
                            >
                                {busy ? "Проводим…" : "Провести"}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </section>
    );
}
